/*
:Page: Change Trip Date Travel Insurance
*/
import { Pencil } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  processFlightTicket,
  transformOCRData,
} from "../../../../api/ocrServices";
import DateRangeInput from "../../../../components/common/DateRangeInput/dateRangeInput";
import FileUploadInput from "../../../../components/common/newFileUploadInput/FileUploadInput";
import FormTabs from "../../../../components/common/tabs/formTabs";
import TitleDescription from "../../../../components/newMotorPolicy/titleDescription/titleDescription";
import TravelBottomStickyBtnLayout from "../../../../components/newTravelPolicy/layouts/TravelBottomStickyBtnLayout";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import PolicyCancellationMotorLayout from "../../../motorInsurancePages/policyOverview/managePolicy/policyCancellationFlow/policyCancellationMotorLayout";

// OCR Result Screen Component for Change Trip Date
const ChangeTripDateOCRResult = ({ formData, setFormData, onBack }) => {
  const translate = useLanguageTranslate();
  const navigate = useNavigate();

  // Format date as DD/MM/YYYY for both English and Arabic
  const formatDate = (date) => {
    if (!date || date === "N/A") return "N/A";

    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return "N/A";

    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const day = String(dateObj.getDate()).padStart(2, "0");

    return `${day}/${month}/${year}`;
  };

  // Parse DD/MM/YYYY to Date object
  const parseDateString = (dateString) => {
    if (!dateString || dateString === "N/A") return null;
    const parts = dateString.split("/");
    if (parts.length !== 3) return null;
    return new Date(parts[2], parts[1] - 1, parts[0]);
  };

  // Calculate days between two DD/MM/YYYY date strings
  const calculateDaysBetween = (departureDateStr, returnDateStr) => {
    const depDate = parseDateString(departureDateStr);
    const retDate = parseDateString(returnDateStr);
    if (!depDate || !retDate) return 0;
    const diffTime = retDate.getTime() - depDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays > 0 ? diffDays : 0;
  };

  // Extract data from OCR results
  const extractTravelDetails = () => {
    const ocrData = formData.ocrData;

    if (!ocrData || !ocrData.output || !ocrData.output.data) {
      return {
        days: 0,
        passengers: 1,
        departureDate: "N/A",
        returnDate: "N/A",
        destination: "N/A",
        airline: "N/A",
        flightNumber: "N/A",
      };
    }

    const data = ocrData.output.data;
    const flights = data.flights || [];

    const passengers = data.numberOfPassengers || 1;

    // Get departure date from first flight
    const departureDate =
      flights.length > 0 ? formatDate(flights[0].departureDate) : "N/A";

    // Get return date from last flight
    const returnDate =
      flights.length > 0
        ? formatDate(flights[flights.length - 1].arrivalDate)
        : "N/A";

    // Calculate days from dates - fallback to OCR value only if dates unavailable
    const days = calculateDaysBetween(departureDate, returnDate) || data.durationOfTravelInDays || 0;

    const destination = flights.length > 0 ? flights[0]?.to : "N/A";
    const airline = flights.length > 0 ? flights[0]?.airline : "N/A";
    const flightNumber = flights.length > 0 ? flights[0]?.flight : "N/A";

    return {
      days,
      passengers,
      departureDate,
      returnDate,
      destination,
      airline,
      flightNumber,
    };
  };

  const travelDetails = extractTravelDetails();

  // Check if departure date is in the past
  const isDateInPast = (dateString) => {
    if (!dateString || dateString === "N/A") return false;
    const parts = dateString.split("/");
    if (parts.length !== 3) return false;
    const departureDate = new Date(parts[2], parts[1] - 1, parts[0]);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return departureDate < today;
  };

  // Check if return date exceeds one year from departure date
  const isReturnDateExceedsOneYear = () => {
    const depDate = parseDateString(travelDetails.departureDate);
    const retDate = parseDateString(travelDetails.returnDate);
    if (!depDate || !retDate) return false;
    const oneYearAfter = new Date(depDate);
    oneYearAfter.setFullYear(oneYearAfter.getFullYear() + 1);
    oneYearAfter.setDate(oneYearAfter.getDate() - 1);
    return retDate > oneYearAfter;
  };

  const isDepartureInPast = isDateInPast(travelDetails.departureDate);
  const isExceedsOneYear = isReturnDateExceedsOneYear();

  const handleEditTripInformation = () => {
    setFormData((prev) => ({
      ...prev,
      showOCRResult: false,
      showEditScreen: true,
    }));
  };

  // Back button handler - go back to upload screen
  const handleBackClick = () => {
    if (onBack) {
      onBack();
    } else {
      setFormData((prev) => ({
        ...prev,
        showOCRResult: false,
        uploadedFiles: [],
        mockDataPopulated: false,
        mockDataSource: null,
        ocrData: null,
        ocrProcessing: false,
      }));
    }
  };

  const handleContinueClick = () => {
    // Button is already disabled when isDepartureInPast or isExceedsOneYear
    // Inline warnings are shown in the UI — no need for alert/toast here
    console.log("Continue with OCR data:", formData);

    // Navigate back to policy overview
    navigate(-1);

    // Show success toast (after navigation, like other endorsements)
    toast.success(
      translate(
        "Trip date change request submitted successfully!",
        "تم إرسال طلب تغيير تاريخ السفر بنجاح!"
      )
    );
  };

  return (
    <PolicyCancellationMotorLayout
      is_hide_back_btn={false}
      show_close_btn={false}
      isFirstStep={false}
      backClickHandler={handleBackClick}
      is_bottom_sticky_layout={false}
    >
      <TravelBottomStickyBtnLayout
        btn_onClick={handleContinueClick}
        btn_disabled={isDepartureInPast || isExceedsOneYear}
        btn_label={translate("Confirm", "تأكيد")}
      >
        <div className="w-[32%] max767:w-full mx-auto ipad:min-w-[66%] pb-28 max767:px-4 max767:pb-20 pt-[1%]">
          <TitleDescription
            title={translate("Change Trip Date", "تغيير تاريخ السفر")}
            description={translate(
              "Please provide the necessary information below.",
              "يُرجى تقديم المعلومات اللازمة أدناه."
            )}
            className="max767:-mt-4"
          />

          <div className="w-full mb-6 bg-[#F6F8F9] p-4 max767:p-0 max767:bg-transparent rounded-xl">
            {/* Travel Details Section - EXACT COPY */}
            <div className="mb-6">
              <h3 className="text-[#1A2024] text-base font-semibold mb-3 max767:mb-1 max767:mt-2">
                {translate("Travel Details", "بيانات السفر")}
              </h3>
              <p className="text-[#1A2024] text-sm mb-6">
                {translate(
                  "Please confirm the details below are correct for your travel.",
                  "يرجى التأكد من صحة تفاصيل سفرك أدناه."
                )}
              </p>

              {/* Travel Details Table - EXACT COPY */}
              <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
                {/* Days */}
                <div className="flex border-b border-[#E5E7EB] h-[44px] leading-[44px]">
                  <div className="flex-1 px-4 bg-white border-r rtl:border-l border-[#E5E7EB]">
                    <span className="text-[#5B6871] text-sm font-medium">
                      {translate("Days", "عدد الأيام")}
                    </span>
                  </div>
                  <div className="flex-1 px-4 bg-white">
                    <span className="text-[#1A2024] text-sm font-medium">
                      {travelDetails.days}
                    </span>
                  </div>
                </div>

                {/* Passengers */}
                <div className="flex border-b border-[#E5E7EB] h-[44px] leading-[44px]">
                  <div className="flex-1 px-4 bg-white border-r rtl:border-l border-[#E5E7EB]">
                    <span className="text-[#5B6871] text-sm font-medium">
                      {translate("Passengers", "عدد المسافرين")}
                    </span>
                  </div>
                  <div className="flex-1 px-4 bg-white">
                    <span className="text-[#1A2024] text-sm font-medium">
                      {travelDetails.passengers}
                    </span>
                  </div>
                </div>

                {/* Departure Date */}
                <div className="flex border-b border-[#E5E7EB] h-[44px] leading-[44px]">
                  <div className="flex-1 px-4 bg-white border-r rtl:border-l border-[#E5E7EB]">
                    <span className="text-[#5B6871] text-sm font-medium">
                      {translate("Departure date", "تاريخ المغادرة")}
                    </span>
                  </div>
                  <div className="flex-1 px-4 bg-white">
                    <span className="text-[#1A2024] text-sm font-medium">
                      {travelDetails.departureDate}
                    </span>
                  </div>
                </div>

                {/* Return Date */}
                <div className="flex border-b border-[#E5E7EB] h-[44px] leading-[44px]">
                  <div className="flex-1 px-4 bg-white border-r rtl:border-l border-[#E5E7EB]">
                    <span className="text-[#5B6871] text-sm font-medium">
                      {translate("Return date", "تاريخ العودة")}
                    </span>
                  </div>
                  <div className="flex-1 px-4 bg-white">
                    <span className="text-[#1A2024] text-sm font-medium">
                      {travelDetails.returnDate}
                    </span>
                  </div>
                </div>

                {/* Airline */}
                <div className="flex border-b border-[#E5E7EB] h-[44px] leading-[44px]">
                  <div className="flex-1 px-4 bg-white border-r rtl:border-l border-[#E5E7EB]">
                    <span className="text-[#5B6871] text-sm font-medium">
                      {translate("Airline", "الخطوط الجوية")}
                    </span>
                  </div>
                  <div className="flex-1 px-4 bg-white">
                    <span className="text-[#1A2024] text-sm font-medium">
                      {travelDetails.airline}
                    </span>
                  </div>
                </div>

                {/* Flight Number */}
                <div className="flex border-b border-[#E5E7EB] h-[44px] leading-[44px]">
                  <div className="flex-1 px-4 bg-white border-r rtl:border-l border-[#E5E7EB]">
                    <span className="text-[#5B6871] text-sm font-medium">
                      {translate("Flight number", "رقم الرحلة")}
                    </span>
                  </div>
                  <div className="flex-1 px-4 bg-white">
                    <span className="text-[#1A2024] text-sm font-medium">
                      {travelDetails.flightNumber}
                    </span>
                  </div>
                </div>
              </div>

              {/* Warning for past dates - EXACT COPY */}
              {isDepartureInPast && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg
                        className="w-5 h-5 text-red-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3 rtl:mr-3 rtl:ml-0">
                      <h3 className="text-sm font-medium text-red-800">
                        {translate(
                          "Invalid Flight Ticket",
                          "تذكرة طيران غير صالحة"
                        )}
                      </h3>
                      <p className="mt-1 text-sm text-red-700">
                        {translate(
                          "The departure date is in the past. Please edit your trip information with valid travel dates.",
                          "تاريخ المغادرة في الماضي. يرجى تعديل معلومات رحلتك بتواريخ سفر صالحة."
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Warning for one year rule violation */}
              {isExceedsOneYear && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg
                        className="w-5 h-5 text-red-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3 rtl:mr-3 rtl:ml-0">
                      <h3 className="text-sm font-medium text-red-800">
                        {translate(
                          "Travel Duration Exceeds One Year",
                          "مدة السفر تتجاوز سنة واحدة"
                        )}
                      </h3>
                      <p className="mt-1 text-sm text-red-700">
                        {translate(
                          "The return date cannot be more than one year after the departure date. Please edit your trip information.",
                          "لا يمكن أن يتجاوز تاريخ العودة سنة واحدة بعد تاريخ المغادرة. يرجى تعديل معلومات رحلتك."
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Edit Trip Information Button */}
              <button
                onClick={handleEditTripInformation}
                className="mt-4 mb-6 max767:!mb-20 inline-flex items-center text-[#1A2024] bg-white hover:bg-gray-50 text-sm font-semibold px-4 py-2 rounded-lg border border-[#E5E7EB] rtl:flex-row-reverse transition-colors"
              >
                <Pencil className="w-4 h-4 mr-2 rtl:ml-0 rtl:mr-2" />
                {translate("Edit Trip Information", "تغيير بيانات السفر")}
              </button>
            </div>
          </div>
        </div>
      </TravelBottomStickyBtnLayout>
    </PolicyCancellationMotorLayout>
  );
};

// Simple Edit Screen Component - COPY from EditTripInfo
const ChangeTripDateEditScreen = ({ formData, setFormData, onBack }) => {
  const translate = useLanguageTranslate();
  const navigate = useNavigate();
  const [tripData, setTripData] = useState({
    fromDate: formData.fromDate || null,
    toDate: formData.toDate || null,
  });

  const getInitialErrors = () => {
    const from = formData.fromDate;
    const to = formData.toDate;
    if (from && to) {
      const oneYearAfter = new Date(from);
      oneYearAfter.setFullYear(oneYearAfter.getFullYear() + 1);
      oneYearAfter.setDate(oneYearAfter.getDate() - 1);
      if (new Date(to) > oneYearAfter) {
        return {
          toDate: translate(
            "The return date cannot be more than one year after the departure date.",
            "لا يمكن أن يتجاوز تاريخ العودة سنة واحدة بعد تاريخ المغادرة."
          ),
        };
      }
    }
    return {};
  };
  const [errors, setErrors] = useState(getInitialErrors);

  const handleDateSelect = (startDate, endDate) => {
    setTripData((prev) => ({
      ...prev,
      fromDate: startDate,
      toDate: endDate,
    }));

    // Real-time validation for > 1 year
    if (startDate && endDate) {
      const oneYearAfter = new Date(startDate);
      oneYearAfter.setFullYear(oneYearAfter.getFullYear() + 1);
      oneYearAfter.setDate(oneYearAfter.getDate() - 1);
      if (endDate > oneYearAfter) {
        setErrors({
          toDate: translate(
            "The return date cannot be more than one year after the departure date.",
            "لا يمكن أن يتجاوز تاريخ العودة سنة واحدة بعد تاريخ المغادرة."
          ),
        });
      } else {
        setErrors({});
      }
    } else {
      setErrors({});
    }
  };

  // Check if departure date is in the past
  const isDateInPast = (date) => {
    if (!date) return false;
    const departureDate = date instanceof Date ? date : new Date(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return departureDate < today;
  };

  // Check if return date exceeds one year from departure date
  const isReturnDateExceedsOneYear = () => {
    if (!tripData.fromDate || !tripData.toDate) return false;
    const oneYearAfter = new Date(tripData.fromDate);
    oneYearAfter.setFullYear(oneYearAfter.getFullYear() + 1);
    oneYearAfter.setDate(oneYearAfter.getDate() - 1);
    return tripData.toDate > oneYearAfter;
  };

  const handleSave = () => {
    // Button is already disabled when dates are invalid — just save and proceed

    // Save changes and directly proceed to submit (like new travel policy flow)
    // Update the ocrData with edited dates
    const updatedOcrData = formData.ocrData ? {
      ...formData.ocrData,
      output: {
        ...formData.ocrData.output,
        data: {
          ...formData.ocrData.output.data,
          // Calculate new duration in days
          durationOfTravelInDays: tripData.fromDate && tripData.toDate
            ? Math.ceil((tripData.toDate - tripData.fromDate) / (1000 * 60 * 60 * 24)) + 1
            : formData.ocrData.output.data.durationOfTravelInDays,
          flights: formData.ocrData.output.data.flights?.map((flight, index) => {
            if (index === 0) {
              return {
                ...flight,
                departureDate: tripData.fromDate ? tripData.fromDate.toISOString() : flight.departureDate,
              };
            }
            if (index === formData.ocrData.output.data.flights.length - 1) {
              return {
                ...flight,
                arrivalDate: tripData.toDate ? tripData.toDate.toISOString() : flight.arrivalDate,
              };
            }
            return flight;
          }) || [],
        },
      },
    } : formData.ocrData;

    // Update form data
    setFormData((prev) => ({
      ...prev,
      fromDate: tripData.fromDate,
      toDate: tripData.toDate,
      ocrData: updatedOcrData,
      showEditScreen: false,
      showOCRResult: false,
    }));

    // Directly proceed to submit - show toast and navigate
    console.log("Continue with edited OCR data:", { ...formData, fromDate: tripData.fromDate, toDate: tripData.toDate });

    // Navigate back to policy overview
    navigate(-1);

    // Show success toast (after navigation, like other endorsements)
    toast.success(
      translate(
        "Trip date change request submitted successfully!",
        "تم إرسال طلب تغيير تاريخ السفر بنجاح!"
      )
    );
  };

  return (
    <PolicyCancellationMotorLayout
      is_hide_back_btn={false}
      show_close_btn={false}
      isFirstStep={false}
      backClickHandler={onBack}
      is_bottom_sticky_layout={false}
    >
      <TravelBottomStickyBtnLayout
        btn_onClick={handleSave}
        btn_disabled={!tripData.fromDate || !tripData.toDate || isDateInPast(tripData.fromDate)}
        btn_label={translate("Confirm", "تأكيد")}
      >
        <div className="w-[32%] max767:w-full mx-auto ipad:min-w-[66%] pb-5 max767:px-4 max767:pb-20 pt-[1%]">
          <TitleDescription
            title={translate("Edit Trip Information", "تغيير تاريخ السفر")}
            description={translate(
              "Update your travel dates.",
              "يُرجى تقديم المعلومات اللازمة أدناه."
            )}
            className="max767:-mt-4"
          />

          <div className="space-y-6 mb-8 max767:!mb-20">
            {/* Travel Dates */}
            <div>
              <DateRangeInput
                onDateSelect={handleDateSelect}
                initialStartDate={tripData.fromDate}
                initialEndDate={tripData.toDate}
                fromPlaceholder={translate("Departure date", "تاريخ المغادرة")}
                toPlaceholder={translate("Return date", "تاريخ العودة")}
                fromLabel={translate("From", "من")}
                toLabel={translate("To", "إلى")}
                inline={false}
              />
              {errors.toDate && (
                <p className="text-red-500 text-sm mt-2">{errors.toDate}</p>
              )}
            </div>
          </div>
        </div>
      </TravelBottomStickyBtnLayout>
    </PolicyCancellationMotorLayout>
  );
};

const ChangeTripDateTravel = () => {
  const { policy_id } = useParams();
  const translate = useLanguageTranslate();
  const navigate = useNavigate();

  // Navigation function to policy overview
  const handleNavigationToPolicyOverview = () => {
    // For change trip date, we can implement a simple back navigation or policy overview navigation
    // Since this is an endorsement, we should go back to policy overview
    window.history.back();
  };

  // Form state - EXACT STRUCTURE FROM dateSelection.jsx
  const [formData, setFormData] = useState({
    hasFlightTickets: null, // null = no selection, true = yes, false = no
    flightNumber: "",
    fromDate: null,
    toDate: null,
    // Enhanced state structure for conditional form display - SAME AS dateSelection.jsx
    uploadedFiles: [],
    mockDataPopulated: false,
    mockDataSource: null, // Track data origin: 'upload' or 'manual'
    isUploading: false,
    uploadError: null,
    // OCR-specific state
    ocrProcessing: false,
    ocrData: null,
    showOCRResult: false,
    showEditScreen: false,
    showOCRProcessingToUser: false,
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Handle flight tickets selection - EXACT LOGIC FROM dateSelection.jsx
  const handleFlightTicketsChange = (hasTickets) => {
    setFormData((prev) => ({
      ...prev,
      hasFlightTickets: hasTickets,
      flightNumber: hasTickets ? prev.flightNumber : "",
      // Reset upload-related state when changing flight ticket status
      uploadedFiles: hasTickets ? prev.uploadedFiles : [],
      uploadError: hasTickets ? prev.uploadError : null,
      mockDataPopulated: hasTickets ? prev.mockDataPopulated : false,
      mockDataSource: hasTickets ? prev.mockDataSource : null,
      // Reset OCR state
      ocrProcessing: hasTickets ? prev.ocrProcessing : false,
      ocrData: hasTickets ? prev.ocrData : null,
      showOCRResult: hasTickets ? prev.showOCRResult : false,
      showOCRProcessingToUser: hasTickets
        ? prev.showOCRProcessingToUser
        : false,
    }));

    // Clear error when user makes selection for immediate UI feedback
    if (errors.hasFlightTickets) {
      setErrors((prev) => ({ ...prev, hasFlightTickets: "" }));
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear field error
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Handle date selection - EXACT VALIDATION FROM dateSelection.jsx
  const handleDateSelect = (startDate, endDate) => {
    setFormData((prev) => ({
      ...prev,
      fromDate: startDate,
      toDate: endDate,
    }));

    // Real-time validation - same as "about your trip" screen
    if (startDate && endDate) {
      const newErrors = { ...errors };

      // Clear existing date errors if dates are valid
      if (endDate > startDate) {
        // Check one-year validation rule
        const oneYearAfterDeparture = new Date(startDate);
        oneYearAfterDeparture.setFullYear(
          oneYearAfterDeparture.getFullYear() + 1
        );
        oneYearAfterDeparture.setDate(oneYearAfterDeparture.getDate() - 1);

        if (endDate > oneYearAfterDeparture) {
          newErrors.toDate = translate(
            "The return date cannot be more than one year after the departure date.",
            "لا يمكن أن يتجاوز تاريخ العودة سنة واحدة بعد تاريخ المغادرة."
          );
        } else {
          // Valid date range, clear errors
          newErrors.fromDate = "";
          newErrors.toDate = "";
        }
      } else {
        // End date is not after start date
        newErrors.toDate = translate(
          "To date must be after from date",
          "تاريخ العودة يجب أن يكون بعد تاريخ الذهاب"
        );
      }

      setErrors(newErrors);
    } else {
      // Clear errors when user selects dates for immediate UI feedback
      if (errors.fromDate || errors.toDate) {
        setErrors((prev) => ({
          ...prev,
          fromDate: startDate ? "" : prev.fromDate,
          toDate: endDate ? "" : prev.toDate,
        }));
      }
    }
  };

  // Handle file upload
  const handleFileUpload = async (fileData) => {
    console.log("📄 Flight ticket file uploaded:", fileData);

    if (!fileData || fileData.error) {
      toast.error(
        translate(
          "Failed to upload file. Please try again.",
          "فشل في تحميل الملف. يرجى المحاولة مرة أخرى."
        )
      );
      return;
    }

    // Add file to uploaded files
    setFormData((prev) => ({
      ...prev,
      uploadedFiles: [...prev.uploadedFiles, fileData],
      uploadError: null,
    }));

    // Start OCR processing
    if (fileData.file) {
      processOCR(fileData.file);
    }
  };

  // Process OCR in background (same logic as dateSelection but show results in blocks)
  const processOCR = async (file) => {
    setFormData((prev) => ({
      ...prev,
      ocrProcessing: true,
    }));

    try {
      console.log("🔄 Starting flight ticket OCR processing...");
      const result = await processFlightTicket(file);

      if (result.success && result.data?.output?.status) {
        console.log("✅ OCR processing successful:", result.data);

        const transformedData = transformOCRData(result.data);

        setFormData((prev) => ({
          ...prev,
          ocrProcessing: false,
          ocrData: result.data,
          mockDataPopulated: true,
          mockDataSource: "upload",
          showOCRResult: true,
          // Pre-fill form fields with extracted data
          flightNumber: transformedData.firstFlightNumber || prev.flightNumber,
          fromDate: transformedData.fromDate || prev.fromDate,
          toDate: transformedData.toDate || prev.toDate,
        }));

        toast.success(
          translate(
            "Flight ticket data extracted successfully!",
            "تم استخراج بيانات تذكرة الطيران بنجاح!"
          )
        );
      } else {
        console.warn("❌ OCR processing failed:", result.error || result.data);

        setFormData((prev) => ({
          ...prev,
          ocrProcessing: false,
        }));

        toast.error(
          translate(
            "Failed to extract ticket data. Please fill the form manually.",
            "فشل في استخراج بيانات التذكرة. يرجى ملء النموذج يدوياً."
          )
        );
      }
    } catch (error) {
      console.error("❌ OCR processing error:", error);

      setFormData((prev) => ({
        ...prev,
        ocrProcessing: false,
      }));

      toast.error(
        translate(
          "Error processing ticket. Please try again or fill manually.",
          "خطأ في معالجة التذكرة. يرجى المحاولة مرة أخرى أو التعبئة يدوياً."
        )
      );
    }
  };

  // Handle file removal - EXACT LOGIC FROM dateSelection.jsx
  const handleFileRemove = () => {
    setFormData((prev) => ({
      ...prev,
      uploadedFiles: [],
      mockDataPopulated: false,
      mockDataSource: null,
      ocrData: null,
      showOCRResult: false,
      ocrProcessing: false,
      showOCRProcessingToUser: false,
      uploadError: null,
      // Clear the form fields too when file is removed
      flightNumber: "",
      fromDate: null,
      toDate: null,
    }));
  };

  // Validation function for submission - EXACT LOGIC FROM dateSelection.jsx
  const validateForm = () => {
    const newErrors = {};

    if (formData.hasFlightTickets === null) {
      newErrors.hasFlightTickets = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    }

    if (formData.hasFlightTickets === false) {
      // Manual entry validation
      if (!formData.fromDate) {
        newErrors.fromDate = translate(
          "This field is required.",
          "هذا الحقل مطلوب."
        );
      }

      if (!formData.toDate) {
        newErrors.toDate = translate(
          "This field is required.",
          "هذا الحقل مطلوب."
        );
      }

      // Cross-field validations for manual entry - same as "about your trip"
      if (formData.fromDate && formData.toDate) {
        // Validate that to date is after from date
        if (formData.toDate <= formData.fromDate) {
          newErrors.toDate = translate(
            "To date must be after from date.",
            "تاريخ العودة يجب أن يكون بعد تاريخ الذهاب."
          );
        }

        // Business rule: Maximum travel duration validation (one year)
        const oneYearAfterDeparture = new Date(formData.fromDate);
        oneYearAfterDeparture.setFullYear(
          oneYearAfterDeparture.getFullYear() + 1
        );
        oneYearAfterDeparture.setDate(oneYearAfterDeparture.getDate() - 1);
        if (formData.toDate > oneYearAfterDeparture) {
          newErrors.toDate = translate(
            "The return date cannot be more than one year after the departure date.",
            "لا يمكن أن يتجاوز تاريخ العودة سنة واحدة بعد تاريخ المغادرة."
          );
        }
      }
    }

    // Validation for upload path with populated data
    if (formData.hasFlightTickets === true && formData.mockDataPopulated) {
      // Date validation
      if (!formData.fromDate) {
        newErrors.fromDate = translate(
          "This field is required.",
          "هذا الحقل مطلوب."
        );
      }

      if (!formData.toDate) {
        newErrors.toDate = translate(
          "This field is required.",
          "هذا الحقل مطلوب."
        );
      }

      // Cross-field date validation
      if (formData.fromDate && formData.toDate) {
        if (formData.toDate <= formData.fromDate) {
          newErrors.toDate = translate(
            "To date must be after from date.",
            "تاريخ العودة يجب أن يكون بعد تاريخ الذهاب."
          );
        }

        // Business rule: Maximum travel duration validation (one year)
        const oneYearAfterDeparture = new Date(formData.fromDate);
        oneYearAfterDeparture.setFullYear(
          oneYearAfterDeparture.getFullYear() + 1
        );
        oneYearAfterDeparture.setDate(oneYearAfterDeparture.getDate() - 1);
        if (formData.toDate > oneYearAfterDeparture) {
          newErrors.toDate = translate(
            "The return date cannot be more than one year after the departure date.",
            "لا يمكن أن يتجاوز تاريخ العودة سنة واحدة بعد تاريخ المغادرة."
          );
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Helper: check if selected dates violate the one-year rule
  const isDatesExceedOneYear = (fromDate, toDate) => {
    if (!fromDate || !toDate) return false;
    const oneYearAfter = new Date(fromDate);
    oneYearAfter.setFullYear(oneYearAfter.getFullYear() + 1);
    oneYearAfter.setDate(oneYearAfter.getDate() - 1);
    return toDate > oneYearAfter;
  };

  // Check if form is valid for button state (without setting errors)
  const isFormValid = () => {
    if (formData.hasFlightTickets === null) return false;

    if (formData.hasFlightTickets === false) {
      // Manual entry path - need both dates, departure not in past
      // Note: > 1 year case shows error but button stays enabled
      if (!formData.fromDate || !formData.toDate) return false;
      const depDate = new Date(formData.fromDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (depDate < today) return false;
      return true;
    }

    if (formData.hasFlightTickets === true) {
      if (!formData.mockDataPopulated) return false;
      if (!formData.fromDate || !formData.toDate) return false;
      const depDate = new Date(formData.fromDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (depDate < today) return false;
      return true;
    }

    return false;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      // Here you would call the change trip date API
      console.log("🚀 Submitting change trip date request:", {
        policyId: policy_id,
        hasFlightTickets: formData.hasFlightTickets,
        flightNumber: formData.flightNumber,
        fromDate: formData.fromDate,
        toDate: formData.toDate,
        ocrData: formData.ocrData,
      });

      // TODO: Implement API call
      // const response = await changeTripDate({ ... });

      // Navigate back to policy overview
      navigate(-1);

      // Show success toast (after navigation, like other endorsements)
      toast.success(
        translate(
          "Trip date change request submitted successfully!",
          "تم إرسال طلب تغيير تاريخ السفر بنجاح!"
        )
      );
    } catch (error) {
      console.error("❌ Error submitting change request:", error);
      toast.error(
        translate(
          "Failed to submit request. Please try again.",
          "فشل في إرسال الطلب. يرجى المحاولة مرة أخرى."
        )
      );
    } finally {
      setIsLoading(false);
    }
  };

  // OCR Result Screen - EXACT COPY from TravelOCRResult
  if (formData.showOCRResult && formData.ocrData) {
    return (
      <ChangeTripDateOCRResult formData={formData} setFormData={setFormData} />
    );
  }

  // Edit Screen - COPY from EditTripInfo
  if (formData.showEditScreen && formData.ocrData) {
    const handleBackToOCRResult = () => {
      setFormData((prev) => ({
        ...prev,
        showEditScreen: false,
        showOCRResult: true,
      }));
    };
    return (
      <ChangeTripDateEditScreen formData={formData} setFormData={setFormData} onBack={handleBackToOCRResult} />
    );
  }

  return (
    <PolicyCancellationMotorLayout
      is_hide_back_btn={false}
      show_close_btn={false}
      isFirstStep={true}
      backClickHandler={handleNavigationToPolicyOverview}
      handleCloseBtnClick={handleNavigationToPolicyOverview}
      is_bottom_sticky_layout={false}
    >
      <TravelBottomStickyBtnLayout
        btn_onClick={handleSubmit}
        btn_disabled={isLoading || !isFormValid()}
        btn_label={translate(
          isLoading ? "Submitting..." : "Confirm",
          isLoading ? "جاري الإرسال..." : "تأكيد"
        )}
      >
        <div className="w-[32%] max767:w-full mx-auto ipad:min-w-[66%] pb-32 max767:px-4 max767:pb-20 pt-[1%]">
          <TitleDescription
            title={translate("Change Trip Date", "تغيير تاريخ السفر")}
            description={translate(
              "Please change the travel date accordingly.",
              "يُرجى تقديم المعلومات اللازمة أدناه."
            )}
          />

          <div className="w-full mb-6 max767:!pb-20">
            {/* Flight Tickets Question */}
            <FormTabs
              label={translate(
                "Do you have a travel flight ticket?",
                "هل لديك تذاكر سفر؟"
              )}
              tabs={[
                {
                  type: true,
                  label_en: "Yes",
                  label_ar: "نعم",
                },
                {
                  type: false,
                  label_en: "No",
                  label_ar: "لا",
                },
              ]}
              activeTab={formData.hasFlightTickets}
              onTabClick={handleFlightTicketsChange}
              error={errors.hasFlightTickets}
              className="!relative !z-0 !mb-6"
            />

            {/* File Upload Section - Show when user has flight tickets but hasn't uploaded files yet - EXACT COPY FROM dateSelection.jsx */}
            {formData.hasFlightTickets === true &&
              !formData.mockDataPopulated && (
                <div className="mb-6 !mt-6">
                  {/* Informative section about why to upload tickets - EXACT COPY FROM dateSelection.jsx */}
                  <div className="mb-6 p-4 bg-[#F0F8F0] border border-[#D1E7DD] rounded-lg">
                    <h3 className="text-[#1A2024] text-lg font-medium mb-3">
                      {translate(
                      "Why upload your tickets?",
                      "لماذا نطلب منك تحميل تذاكرك؟"
                      )}
                    </h3>
                    <div className="space-y-2">
                      <div className="flex items-start space-x-2 rtl:space-x-reverse">
                      <div className="flex-shrink-0 mt-1">
                        <svg
                        className="w-4 h-4 text-green-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                        </svg>
                      </div>
                      <p className="text-sm text-black">
                        {translate(
                        "Auto-fill: Instant ticket scanning saves you from manual data entry",
                        "الملء التلقائي: المسح الفوري للتذاكر يوفر عليك الإدخال اليدوي للبيانات"
                        )}
                      </p>
                      </div>
                      <div className="flex items-start space-x-2 rtl:space-x-reverse">
                      <div className="flex-shrink-0 mt-1">
                        <svg
                        className="w-4 h-4 text-green-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                        </svg>
                      </div>
                      <p className="text-sm text-black">
                        {translate(
                        "Accurate coverage: Get precise protection for your exact travel dates",
                        "تغطية دقيقة: احصل على حماية دقيقة لتواريخ سفرك المحددة"
                        )}
                      </p>
                      </div>
                      <div className="flex items-start space-x-2 rtl:space-x-reverse">
                      <div className="flex-shrink-0 mt-1">
                        <svg
                        className="w-4 h-4 text-green-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                        </svg>
                      </div>
                      <p className="text-sm text-black">
                        {translate(
                        "Faster policy: Get covered in seconds with error-free processing",
                        "سياسة أسرع: احصل على التغطية في ثوانٍ مع معالجة خالية من الأخطاء"
                        )}
                      </p>
                      </div>
                    </div>
                    <p className="text-xs text-[#5B6871] mt-3">
                      {translate(
                      "Your ticket information is used only for policy creation and claims verification.",
                      "يتم استخدام معلومات تذكرتك فقط لإنشاء السياسة والتحقق من المطالبات."
                      )}
                    </p>
                    </div>

                    <div className="mb-4">
                    <label className="text-[#48535B] text-base not-italic font-normal leading-[26px] tracking-[-0.16px] mb-3 block">
                      {translate(
                      "Upload Your Flight Ticket",
                      "قم بتحميل تذاكر الطيران الخاصة بك"
                      )}
                    </label>
                    <p className="text-sm text-[#6B7682] mb-4 max767:hidden">
                      {translate(
                      "Please upload your flight ticket (PDF, JPG, PNG). Maximum 1 file, up to 10 MB.",
                      "يرجى رفع تذكرة الطيران الخاصة بك (PDF، JPG، PNG). حد أقصى 1 ملف، حتى 10 ميجابايت."
                      )}
                    </p>
                    <FileUploadInput
                      name="flight_tickets"
                      onFileSelect={handleFileUpload}
                      onFileRemove={handleFileRemove}
                      validTypes={[
                      "application/pdf",
                      "image/jpeg",
                      "image/png",
                      ]}
                      maxSizeKB={10240} // 10MB limit
                      uploadToServer={true}
                      multiple={true}
                      maxFiles={1}
                      className="mb-4"
                      disabled={
                      formData.isUploading ||
                      formData.ocrProcessing ||
                      (formData.uploadError &&
                        formData.uploadError.canRetry)
                      }
                      initialFiles={formData.uploadedFiles || []}
                    />
                    </div>

                    {/* OCR Processing status - show when OCR is processing after file upload - EXACT COPY */}
                    {formData.ocrProcessing && (
                    <div className="mb-10 p-3 bg-blue-50 border border-blue-200 rounded-lg  max767:mb-20">
                      <div className="flex items-center space-x-2 rtl:space-x-reverse">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      <p className="text-sm text-blue-700">
                        {translate(
                        "Processing ticket data...",
                        "جاري معالجة بيانات التذكرة..."
                        )}
                      </p>
                      </div>
                      <p className="text-xs text-blue-600 mt-1">
                      {translate(
                        "Please wait while we extract your travel information...",
                        "يرجى الانتظار بينما نستخرج معلومات السفر الخاصة بك..."
                      )}
                      </p>
                    </div>
                    )}
                  </div>
                )}

            {/* Form fields for users with flight tickets and populated mock data */}
            {formData.hasFlightTickets === true &&
              formData.mockDataPopulated && (
                <>
                  {/* Date Range Selection */}
                  <div className="formField form-input ">
                    <DateRangeInput
                      fromLabel={translate("From", "من")}
                      toLabel={translate("To", "إلى")}
                      fromPlaceholder={translate(
                        "Select departure date",
                        "اختر تاريخ المغادرة"
                      )}
                      toPlaceholder={translate(
                        "Select return date",
                        "اختر تاريخ العودة"
                      )}
                      onDateSelect={handleDateSelect}
                      initialStartDate={formData.fromDate}
                      initialEndDate={formData.toDate}
                      inline={false}
                    />
                    {(errors.fromDate || errors.toDate) && (
                      <p className="field-err text-red-500 text-sm mt-1 text-left rtl:text-right">
                        {errors.fromDate || errors.toDate}
                      </p>
                    )}
                    </div>
                  </>
                )}

            {/* Manual entry form - only show when user selects "No" for flight tickets */}
            {formData.hasFlightTickets === false && (
              <>
                {/* Date Range Selection only */}
                <div className="formField form-input  mb-4">
                  <DateRangeInput
                    fromLabel={translate("From", "من")}
                    toLabel={translate("To", "إلى")}
                    fromPlaceholder={translate(
                      "Select departure date",
                      "اختر تاريخ المغادرة"
                    )}
                    toPlaceholder={translate(
                      "Select return date",
                      "اختر تاريخ العودة"
                    )}
                    onDateSelect={handleDateSelect}
                    initialStartDate={formData.fromDate}
                    initialEndDate={formData.toDate}
                    inline={false}
                  />
                  {(errors.fromDate || errors.toDate) && (
                    <p className="field-err text-red-500 text-sm mt-1 text-left rtl:text-right">
                      {errors.fromDate || errors.toDate}
                    </p>
                  )}
                  </div>
                </>
              )}
            </div>
        </div>
      </TravelBottomStickyBtnLayout>
    </PolicyCancellationMotorLayout>
  );
};

export default ChangeTripDateTravel;
